import { useMemo, useState, type ReactNode } from 'react'
import { useQuery } from '@tanstack/react-query'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Container from '@mui/material/Container'
import Divider from '@mui/material/Divider'
import LinearProgress from '@mui/material/LinearProgress'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

type AssetType =
  | 'exchange' | 'cabinet' | 'dp' | 'joint'
  | 'boiler_family' | 'boiler_model' | 'boiler_unit'
  | 'substation' | 'feeder' | 'meter' | 'comms_hub' | 'other'

type Asset = {
  id: string
  external_ref: string
  display_name: string
  asset_type: AssetType
  parent_id?: string | null
  attributes: Record<string, unknown>
  is_active: boolean
  direct_jobs: number
  direct_open: number
  rolled_jobs: number
  rolled_open: number
}

type AssetDetail = {
  asset: Asset
  children: Asset[]
  jobs: Array<{
    job_id: string
    external_ref: string
    status: string
    priority: string
    job_type_code: string
    scheduled_start: string
    actual_end?: string | null
    engineer_name?: string
    via: 'asset' | 'premises'
  }>
  exceptions: Array<{
    exception_id: string
    code: string
    severity: 'info' | 'warn' | 'high' | 'critical'
    opened_at: string
    suggested_action: string
    subject_summary: string
  }>
}

type TreeNode = Asset & { children: TreeNode[] }

const LIST_KEY = ['analytics', 'asset-health'] as const
const detailKey = (id: string) => ['analytics', 'asset-health', id] as const

export default function AssetHealthPage() {
  const { accessToken } = useAuth()
  const client = useMemo(() => makeClient(() => accessToken), [accessToken])
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const listQ = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => {
      const { data } = await client.callEnvelope<Asset[]>('/api/v1/analytics/asset-health')
      return data ?? []
    },
    refetchInterval: 30_000,
  })

  const detailQ = useQuery({
    queryKey: detailKey(selectedId ?? ''),
    enabled: !!selectedId,
    queryFn: () => client.call<AssetDetail>(`/api/v1/analytics/asset-health/${selectedId}`),
  })

  const tree = useMemo<TreeNode[]>(() => buildTree(listQ.data ?? []), [listQ.data])
  const kpis = useMemo(() => computeKpis(listQ.data ?? []), [listQ.data])

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
        <Stack spacing={2}>
          <KpiStrip kpis={kpis} />

          {listQ.isLoading && <LinearProgress />}
          {listQ.isError && (
            <Alert severity="error">
              {listQ.error instanceof Error ? listQ.error.message : 'Failed to load asset tree.'}
            </Alert>
          )}

          {listQ.data && listQ.data.length === 0 && (
            <Alert severity="info">
              No assets in this tenant yet. Load some via the WFM adapter (or run <code>go run ./cmd/seed</code>).
            </Alert>
          )}

          {listQ.data && listQ.data.length > 0 && (
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ alignItems: 'flex-start' }}>
              <Paper sx={{ width: { xs: '100%', md: 380 }, flexShrink: 0, maxHeight: '75vh', overflowY: 'auto' }}>
                <Box sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Network tree</Typography>
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    Click any node to see its detail. Rolled counts include the subtree.
                  </Typography>
                </Box>
                <Stack sx={{ p: 0.5 }}>
                  {tree.map((n) => (
                    <TreeRow key={n.id} node={n} depth={0} selectedId={selectedId} onSelect={setSelectedId} />
                  ))}
                </Stack>
              </Paper>

              <Box sx={{ flex: 1, minWidth: 0, width: '100%' }}>
                {!selectedId && (
                  <Paper sx={{ p: 3, color: 'text.secondary' }}>
                    Select an asset from the tree to see its jobs, open exceptions, and immediate children.
                  </Paper>
                )}
                {selectedId && detailQ.isLoading && <Paper sx={{ p: 3 }}>Loading detail…</Paper>}
                {selectedId && detailQ.isError && (
                  <Alert severity="error">
                    {detailQ.error instanceof Error ? detailQ.error.message : 'Failed to load asset detail.'}
                  </Alert>
                )}
                {detailQ.data && <DetailPanel detail={detailQ.data} />}
              </Box>
            </Stack>
          )}
        </Stack>
    </Container>
  )
}

// --- KPI strip ------------------------------------------------------------

type Kpis = {
  totalAssets: number
  byType: Partial<Record<AssetType, number>>
  totalOpen: number
  worstAsset: Asset | null
}

function computeKpis(assets: Asset[]): Kpis {
  const byType: Partial<Record<AssetType, number>> = {}
  let totalOpen = 0
  let worst: Asset | null = null
  for (const a of assets) {
    byType[a.asset_type] = (byType[a.asset_type] ?? 0) + 1
    totalOpen += a.direct_open
    if (!worst || a.rolled_open > worst.rolled_open) worst = a
  }
  return { totalAssets: assets.length, byType, totalOpen, worstAsset: worst }
}

function KpiStrip({ kpis }: { kpis: Kpis }) {
  const typeOrder: AssetType[] = [
    'exchange', 'cabinet', 'dp', 'joint',
    'boiler_family', 'boiler_model', 'boiler_unit',
    'substation', 'feeder', 'meter', 'comms_hub', 'other',
  ]
  const worstLabel =
    kpis.worstAsset && kpis.worstAsset.rolled_open > 0
      ? `${kpis.worstAsset.display_name} · ${kpis.worstAsset.rolled_open} open`
      : '—'

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} sx={{ alignItems: { md: 'center' }, flexWrap: 'wrap' }}>
        <Kpi label="Total assets" value={kpis.totalAssets.toLocaleString()} />
        <Kpi label="Open exceptions (direct)" value={kpis.totalOpen.toLocaleString()} tone={kpis.totalOpen > 0 ? 'warning' : 'default'} />
        <Kpi label="Worst subtree" value={worstLabel} tone={kpis.worstAsset && kpis.worstAsset.rolled_open > 0 ? 'error' : 'default'} />
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
          {typeOrder.filter((t) => (kpis.byType[t] ?? 0) > 0).map((t) => (
            <Chip key={t} label={`${t} · ${kpis.byType[t]}`} size="small" variant="outlined" />
          ))}
        </Stack>
      </Stack>
    </Paper>
  )
}

function Kpi({ label, value, tone = 'default' }: { label: string; value: ReactNode; tone?: 'default' | 'warning' | 'error' }) {
  const color = tone === 'error' ? 'error.main' : tone === 'warning' ? 'warning.main' : 'text.primary'
  return (
    <Box>
      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{label}</Typography>
      <Typography variant="h6" sx={{ color, fontWeight: 700 }}>{value}</Typography>
    </Box>
  )
}

// --- Tree row -------------------------------------------------------------

function TreeRow({ node, depth, selectedId, onSelect }: {
  node: TreeNode
  depth: number
  selectedId: string | null
  onSelect: (id: string) => void
}) {
  const [open, setOpen] = useState(depth < 1) // roots expanded by default
  const hasChildren = node.children.length > 0
  const isSelected = node.id === selectedId

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          pl: 1 + depth * 1.5,
          pr: 1,
          py: 0.5,
          bgcolor: isSelected ? 'action.selected' : undefined,
          borderRadius: 0.5,
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => onSelect(node.id)}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={(e) => { e.stopPropagation(); setOpen((v) => !v) }}
            sx={{ p: 0.25 }}
          >
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 22 }} />
        )}

        <Chip label={node.asset_type} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />

        <Typography variant="body2" sx={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {node.display_name}
        </Typography>

        {node.rolled_open > 0 && (
          <Chip
            label={node.rolled_open}
            size="small"
            color={node.rolled_open >= 10 ? 'error' : 'warning'}
            sx={{ height: 20, fontSize: '0.7rem', fontWeight: 700 }}
          />
        )}
      </Box>

      {open && hasChildren && node.children.map((c) => (
        <TreeRow key={c.id} node={c} depth={depth + 1} selectedId={selectedId} onSelect={onSelect} />
      ))}
    </>
  )
}

// --- Detail panel ---------------------------------------------------------

function DetailPanel({ detail }: { detail: AssetDetail }) {
  const { asset, children, jobs, exceptions } = detail
  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'baseline', flexWrap: 'wrap' }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>{asset.display_name}</Typography>
          <Chip label={asset.asset_type} size="small" variant="outlined" />
          <Typography variant="body2" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
            {asset.external_ref}
          </Typography>
          {!asset.is_active && <Chip label="INACTIVE" size="small" color="warning" />}
        </Stack>

        <Stack direction="row" spacing={4} sx={{ mt: 2, flexWrap: 'wrap' }}>
          <Kpi label="Direct jobs" value={asset.direct_jobs.toLocaleString()} />
          <Kpi label="Direct open exc." value={asset.direct_open.toLocaleString()} tone={asset.direct_open > 0 ? 'warning' : 'default'} />
          <Kpi label="Rolled jobs" value={asset.rolled_jobs.toLocaleString()} />
          <Kpi label="Rolled open exc." value={asset.rolled_open.toLocaleString()} tone={asset.rolled_open > 0 ? 'error' : 'default'} />
        </Stack>

        {Object.keys(asset.attributes).length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>Attributes</Typography>
            <Box
              component="pre"
              sx={{
                m: 0,
                mt: 0.5,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                whiteSpace: 'pre-wrap',
                color: 'text.secondary',
              }}
            >
              {JSON.stringify(asset.attributes, null, 2)}
            </Box>
          </Box>
        )}
      </Paper>

      {children.length > 0 && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Children ({children.length})</Typography>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {children.map((c) => (
              <Chip key={c.id} label={`${c.display_name} · ${c.asset_type}`} size="small" variant="outlined" />
            ))}
          </Stack>
        </Paper>
      )}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Recent jobs ({jobs.length})</Typography>
        {jobs.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>No jobs linked to this asset or its served premises.</Typography>}
        {jobs.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>WO</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Engineer</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Scheduled</TableCell>
                  <TableCell>Via</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobs.map((j) => (
                  <TableRow key={j.job_id} hover>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{j.external_ref}</Typography>
                    </TableCell>
                    <TableCell>{j.job_type_code}</TableCell>
                    <TableCell>{j.engineer_name || '—'}</TableCell>
                    <TableCell><Chip label={j.status} size="small" variant="outlined" /></TableCell>
                    <TableCell>{formatWhen(j.scheduled_start)}</TableCell>
                    <TableCell><Chip label={j.via} size="small" variant="outlined" /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Open exceptions ({exceptions.length})</Typography>
        {exceptions.length === 0 && <Typography variant="body2" sx={{ color: 'text.secondary' }}>None open at this asset or below.</Typography>}
        {exceptions.length > 0 && (
          <Stack spacing={1}>
            {exceptions.map((e) => (
              <Box key={e.exception_id} sx={{ display: 'flex', gap: 1, alignItems: 'stretch', p: 1, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                <Box sx={{ width: 4, bgcolor: severityColor(e.severity), borderRadius: 1 }} />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Stack direction="row" spacing={1} sx={{ alignItems: 'baseline' }}>
                    <Chip label={e.severity} size="small" color={severityChipColor(e.severity)} />
                    <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>{e.code}</Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>{formatWhen(e.opened_at)}</Typography>
                  </Stack>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>{e.subject_summary}</Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>{e.suggested_action}</Typography>
                </Box>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>
    </Stack>
  )
}

// --- helpers --------------------------------------------------------------

function buildTree(assets: Asset[]): TreeNode[] {
  const byId = new Map<string, TreeNode>()
  const roots: TreeNode[] = []
  for (const a of assets) byId.set(a.id, { ...a, children: [] })
  for (const node of byId.values()) {
    if (node.parent_id && byId.has(node.parent_id)) {
      byId.get(node.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  }
  // Sort children alphabetically within each parent, roots by type then name.
  const sortChildren = (n: TreeNode) => {
    n.children.sort((a, b) => a.display_name.localeCompare(b.display_name))
    n.children.forEach(sortChildren)
  }
  roots.sort((a, b) => a.asset_type.localeCompare(b.asset_type) || a.display_name.localeCompare(b.display_name))
  roots.forEach(sortChildren)
  return roots
}

function formatWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return iso
  }
}

function severityColor(s: string): string {
  switch (s) {
    case 'critical': return '#E5501C'
    case 'high':     return '#E5501C'
    case 'warn':     return '#F59E0B'
    default:         return '#3B82F6'
  }
}

function severityChipColor(s: string): 'error' | 'warning' | 'info' | 'default' {
  switch (s) {
    case 'critical':
    case 'high':
      return 'error'
    case 'warn':
      return 'warning'
    case 'info':
      return 'info'
    default:
      return 'default'
  }
}
